import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiDownload,
  FiExternalLink,
  FiMail,
  FiMenu,
  FiPhone,
  FiX,
} from "react-icons/fi";
import BrandWordmark from "../brand/BrandWordmark";
import { PUBLIC_NAV_LINKS } from "../../constants/publicNavLinks";
import { CONTACT_EMAIL, CONTACT_PHONES, openSupportMail, SUPPORT_MAILTO_HREF } from "../../constants/contact";
import { LOGIN_PORTAL_OPTIONS } from "../../constants/loginPortals";
import {
  RECOVERY_DATA_DOWNLOADS,
  RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES,
} from "../../constants/recoveryDataDownloads";
import recoveryDirectoryService from "../../services/recoveryDirectory.service";
import { saveExcelBlob, getApiErrorMessage } from "../../utils/downloadExcel";
import { loadRazorpayScript, openRazorpayCheckout } from "../../utils/razorpayCheckout";
import "../../styles/brand.css";
import "../../styles/publicLogin.css";

async function payAndDownloadRecoveryData(item) {
  const orderRes = await recoveryDirectoryService.createPublicDownloadOrder(item.kind);
  const order = orderRes?.data?.data || orderRes?.data || {};
  if (!order.orderId || !order.razorpayKeyId) {
    throw new Error("Could not start payment. Please try again.");
  }

  await loadRazorpayScript();
  const checkout = await openRazorpayCheckout({
    key: order.razorpayKeyId,
    orderId: order.orderId,
    amount: order.amount,
    currency: order.currency || "INR",
    description:
      order.description ||
      `${item.label} download — ₹${item.priceRupees || RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES}`,
  });

  const verifyRes = await recoveryDirectoryService.verifyPublicDownloadPayment(item.kind, {
    purchaseId: order.purchaseId,
    razorpay_order_id: checkout.razorpay_order_id,
    razorpay_payment_id: checkout.razorpay_payment_id,
    razorpay_signature: checkout.razorpay_signature,
  });
  const verified = verifyRes?.data?.data || verifyRes?.data || {};
  const token = String(verified.downloadToken || "").trim();
  if (!token) {
    throw new Error("Payment succeeded but download token was missing. Contact support.");
  }

  const response = await recoveryDirectoryService.downloadPublic(item.kind, token);
  await saveExcelBlob(response, item.filename || "recovery-data.xlsx");
}

export default function PublicLoginHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadBusyId, setDownloadBusyId] = useState("");
  const [downloadError, setDownloadError] = useState("");
  const [downloadCounts, setDownloadCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef(null);
  const downloadRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    setDownloadOpen(false);
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [navOpen]);

  const loadDownloadCounts = async () => {
    setCountsLoading(true);
    try {
      const res = await recoveryDirectoryService.publicSummary();
      const items = res?.data?.data?.items || res?.data?.items || [];
      const next = {};
      (Array.isArray(items) ? items : []).forEach((row) => {
        if (row?.id != null) next[row.id] = Number(row.totalRecords) || 0;
      });
      setDownloadCounts(next);
    } catch {
      setDownloadCounts({});
    } finally {
      setCountsLoading(false);
    }
  };

  const openDownloadMenu = () => {
    setMenuOpen(false);
    setDownloadError("");
    setDownloadOpen((open) => {
      const next = !open;
      if (next) loadDownloadCounts();
      return next;
    });
  };

  const pickPortal = (route) => {
    setMenuOpen(false);
    setNavOpen(false);
    navigate(route);
  };

  const pickDownload = async (item) => {
    if (downloadBusyId) return;
    const total = Number(downloadCounts[item.id]);
    if (Number.isFinite(total) && total <= 0) {
      setDownloadError(`No ${item.label} records uploaded yet.`);
      return;
    }
    setDownloadError("");
    setDownloadBusyId(item.id);
    try {
      await payAndDownloadRecoveryData(item);
      setDownloadOpen(false);
      setNavOpen(false);
      loadDownloadCounts();
    } catch (err) {
      if (err?.message === "CHECKOUT_CLOSED") {
        setDownloadError(
          `Payment cancelled. Pay ₹${RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES} to download.`
        );
      } else if (err?.message === "PAYMENT_FAILED") {
        setDownloadError("Payment failed. Please try again.");
      } else {
        setDownloadError(
          await getApiErrorMessage(
            err,
            `Pay ₹${item.priceRupees || RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES} to download ${item.label}.`
          )
        );
      }
    } finally {
      setDownloadBusyId("");
    }
  };

  const isActive = (to) => location.pathname === to;

  const formatCount = (itemId) => {
    if (countsLoading && downloadCounts[itemId] == null) return "Loading…";
    const n = Number(downloadCounts[itemId]);
    if (!Number.isFinite(n)) return "—";
    const label = n === 1 ? "record" : "records";
    return `${n.toLocaleString("en-IN")} ${label}`;
  };

  const downloadMenu = (
    <div className="public-login-dropdown public-login-dropdown--download" role="menu">
      <p className="public-login-dropdown__hint">
        Pay ₹{RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES} per dataset to download excel
      </p>
      <div className="public-login-dropdown__download-list">
        {RECOVERY_DATA_DOWNLOADS.map((item) => {
          const total = Number(downloadCounts[item.id]);
          const empty = Number.isFinite(total) && total <= 0;
          const busy = downloadBusyId === item.id;
          const price = item.priceRupees || RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES;
          return (
            <button
              key={item.id}
              type="button"
              className={`public-login-dropdown__item public-login-dropdown__item--download${
                empty ? " is-empty" : ""
              }${busy ? " is-busy" : ""}`}
              role="menuitem"
              disabled={Boolean(downloadBusyId) || empty}
              onClick={() => pickDownload(item)}
            >
              <span className="public-login-dropdown__text">
                <strong>{busy ? "Opening payment…" : item.label}</strong>
                <small>
                  {formatCount(item.id)}
                  <span className="public-login-dropdown__meta-sep" aria-hidden>
                    ·
                  </span>
                  ₹{price}
                </small>
              </span>
              <span className="public-login-dropdown__dl-icon" aria-hidden>
                <FiDownload />
              </span>
            </button>
          );
        })}
      </div>
      {downloadError ? (
        <p className="public-login-dropdown__error" role="alert">
          {downloadError}
        </p>
      ) : null}
    </div>
  );

  return (
    <header className="public-login-header">
      <div className="public-login-nav">
        <div className="public-login-nav__inner">
          <div className="public-login-nav__left">
            <Link to="/" className="public-login-nav__brand">
              <span className="public-login-nav__logo-icon" aria-hidden>
                FS
              </span>
              <BrandWordmark className="brand-wordmark--on-dark brand-wordmark--header" />
            </Link>

            <nav className="public-login-nav__menu" aria-label="Main">
              {PUBLIC_NAV_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`public-login-nav__link${isActive(item.to) ? " is-active" : ""}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="public-login-nav__actions">
            <a
              href={`tel:${CONTACT_PHONES[0].tel}`}
              className="public-login-nav__icon public-login-nav__icon--phone public-login-nav__icon--hide-xs"
              aria-label={`Call ${CONTACT_PHONES[0].label}`}
            >
              <FiPhone aria-hidden />
              <span>8796814100</span>
            </a>
            <a
              href={SUPPORT_MAILTO_HREF}
              className="public-login-nav__icon public-login-nav__icon--hide-xs"
              aria-label={`Email ${CONTACT_EMAIL}`}
              onClick={openSupportMail}
            >
              <FiMail aria-hidden />
            </a>

            <div className="public-login-nav__cta">
              <div className="public-login-nav__download-wrap" ref={downloadRef}>
                <button
                  type="button"
                  className="public-login-nav__login-btn public-login-nav__download-btn"
                  aria-expanded={downloadOpen}
                  aria-haspopup="true"
                  onClick={openDownloadMenu}
                >
                  <span className="public-login-nav__download-label">Download data</span>
                  <FiChevronDown className={downloadOpen ? "is-open" : ""} aria-hidden />
                </button>

                {downloadOpen ? downloadMenu : null}
              </div>

              <div className="public-login-nav__login-wrap" ref={menuRef}>
                <button
                  type="button"
                  className="public-login-nav__login-btn"
                  aria-expanded={menuOpen}
                  aria-haspopup="true"
                  onClick={() => {
                    setDownloadOpen(false);
                    setMenuOpen((o) => !o);
                  }}
                >
                  Login
                  <FiChevronDown className={menuOpen ? "is-open" : ""} aria-hidden />
                </button>

                {menuOpen && (
                  <div className="public-login-dropdown" role="menu">
                    {LOGIN_PORTAL_OPTIONS.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="public-login-dropdown__item"
                        role="menuitem"
                        onClick={() => pickPortal(item.route)}
                      >
                        <span className="public-login-dropdown__text">
                          <strong>{item.label}</strong>
                          <small>{item.description}</small>
                        </span>
                        <FiExternalLink aria-hidden />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              className="public-login-nav__menu-toggle"
              aria-expanded={navOpen}
              aria-controls="public-mobile-nav"
              aria-label={navOpen ? "Close menu" : "Open menu"}
              onClick={() => {
                setDownloadOpen(false);
                setMenuOpen(false);
                setNavOpen((o) => {
                  const next = !o;
                  if (next) loadDownloadCounts();
                  return next;
                });
              }}
            >
              {navOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </div>

      {navOpen && (
        <button
          type="button"
          className="public-login-nav__backdrop"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
      )}

      <nav
        id="public-mobile-nav"
        className={`public-login-nav__drawer${navOpen ? " is-open" : ""}`}
        aria-label="Mobile"
        aria-hidden={!navOpen}
      >
        <div className="public-login-nav__drawer-links">
          {PUBLIC_NAV_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`public-login-nav__drawer-link${isActive(item.to) ? " is-active" : ""}`}
              onClick={() => setNavOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="public-login-nav__drawer-downloads">
          <p className="public-login-nav__drawer-downloads-title">Download data</p>
          {RECOVERY_DATA_DOWNLOADS.map((item) => {
            const total = Number(downloadCounts[item.id]);
            const empty = Number.isFinite(total) && total <= 0;
            const busy = downloadBusyId === item.id;
            const price = item.priceRupees || RECOVERY_DATA_DOWNLOAD_PRICE_RUPEES;
            return (
              <button
                key={item.id}
                type="button"
                className={`public-login-nav__drawer-download${empty ? " is-empty" : ""}`}
                disabled={Boolean(downloadBusyId) || empty}
                onClick={() => pickDownload(item)}
                onFocus={loadDownloadCounts}
              >
                <span className="public-login-nav__drawer-download-text">
                  <strong>{busy ? "Opening payment…" : item.label}</strong>
                  <small>
                    {formatCount(item.id)} · ₹{price}
                  </small>
                </span>
                <span className="public-login-dropdown__dl-icon" aria-hidden>
                  <FiDownload />
                </span>
              </button>
            );
          })}
          {downloadError ? (
            <p className="public-login-nav__drawer-download-error" role="alert">
              {downloadError}
            </p>
          ) : null}
        </div>

        <div className="public-login-nav__drawer-contact">
          <a href={`tel:${CONTACT_PHONES[0].tel}`}>
            <FiPhone aria-hidden />
            8796814100
          </a>
          <a href={SUPPORT_MAILTO_HREF} onClick={openSupportMail}>
            <FiMail aria-hidden />
            {CONTACT_EMAIL}
          </a>
        </div>
      </nav>
    </header>
  );
}
